import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  Container,
  Stack,
  IconButton,
  Tooltip,
  TextField,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BottlesIcon from "../../../image/pngwing.com (13).png";
import GlassIcon from "../../../image/pngwing.com (7) 2.png";
import { Controller, useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import CustomDataGrid from "../../constant/commondatagrid";
import { reportStyles } from "../../components/reportStyles";
import {
  wholesalerReportRequest,
  wholesalerReportDateSearchRequest,
  wholesalerMonthReportRequest,
  wholesalerWeekReportRequest,
  wholesalerMonthReportDateSearchRequest,
  wholesalerWeekReportDateSearchRequest,
} from "../../../redux/reports/ReportAction";
import Pagination from "../../constant/CommonPagenation";
import { useDispatch, useSelector } from "react-redux";

import {
  TIME_FRAMES,
  CATEGORIES,
  getBarColumns,
  getWholesalerColumns,
  getCustomerColumns,
  barMonthColumns,
  barWeeklyColumns,
  wholesalerWeeklyColumns,
  wholesalerMonthColumns,
  getWholesalerDayTransactionColumns,
} from "./columns";

import {
  preprocessData,
  preprocessBarData,
  preprocessCustomerData,
  calculateSalesMetrics,
  calculateCustomerSalesMetrics,
} from "./preprocessData";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  barMonthReportDateSearchRequest,
  barMonthReportRequest,
  barReportDateSearchRequest,
  barReportRequest,
  barWeekReportDateSearchRequest,
  barWeekReportRequest,
} from "../../../redux/barReport/BarReportAction";
import {
  customerReportRequest,
  customerReportSearchRequest,
} from "../../../redux/customerReport/CustomerReportAction";
import WholesalerDetailsModal from "./wholesalerAdminReport";
import BarDetailsModal from "./barReport";
import { prepareExcelData } from "./Excel";

const SalesReport = () => {
  const {
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    mode: "onBlur",
  });

  const startDate = watch("startDate", "");
  const endDate = watch("endDate", "");

  const [timeFrame, setTimeFrame] = useState(TIME_FRAMES.MONTH);
  const [selectedCategory, setSelectedCategory] = useState(
    CATEGORIES.WHOLESALER
  );
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [emptyList, setEmptyList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSales, setTotalSales] = useState(0);
  const [avgSalesPerDay, setAvgSalesPerDay] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [isDateSearchActive, setIsDateSearchActive] = useState(false);
  const [filteredRecordsCount, setFilteredRecordsCount] = useState(0);
  const [tableColumns, setTableColumns] = useState(getWholesalerColumns);
  const [currentTimeFrame, setCurrentTimeFrame] = useState(TIME_FRAMES.MONTH);

  const dispatch = useDispatch();

  const [selectedWholesaler, setSelectedWholesaler] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBar, setSelectedBar] = useState(null);
  const [isBarModalOpen, setIsBarModalOpen] = useState(false);

  const handleViewDetails = useCallback(
    (row) => {
      setSelectedWholesaler(row);
      setIsModalOpen(true);
      setCurrentTimeFrame(timeFrame);
    },
    [timeFrame]
  );

  const handleViewBarDetails = useCallback(
    (row) => {
      setSelectedBar(row);
      setIsBarModalOpen(true);
      setCurrentTimeFrame(timeFrame);
    },
    [timeFrame]
  );

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWholesaler(null);
  };

  const handleCloseBarModal = () => {
    setIsBarModalOpen(false);
    setSelectedBar(null);
  };

  const wholesalerViewAllState = useSelector(
    (state) => state?.consumption?.viewAllWholesalerReports || {}
  );
  const wholesalerList = wholesalerViewAllState?.data || [];
  const totalRecords = wholesalerViewAllState?.data?.totalRecords || 0;
  const wholesalerPageSize = wholesalerViewAllState?.data?.pagesize || 10;

  const monthWholesalerViewAllState = useSelector(
    (state) => state?.consumption?.monthWholesalerReports || {}
  );
  const monthWholesalerList = monthWholesalerViewAllState?.data || [];
  const monthTotalRecords =
    monthWholesalerViewAllState?.data?.totalRecords || 0;
  const monthWholesalerPageSize =
    monthWholesalerViewAllState?.data?.pagesize || 10;

  const weekWholesalerViewAllState = useSelector(
    (state) => state?.consumption?.weekWholesalerReports || {}
  );
  const weekWholesalerList = weekWholesalerViewAllState?.data || [];
  const weekTotalRecords = weekWholesalerViewAllState?.data?.totalRecords || 0;
  const weekWholesalerPageSize =
    weekWholesalerViewAllState?.data?.pagesize || 10;

  // Date search
  const wholesalerDateSearchState = useSelector(
    (state) => state?.consumption?.dateSearchWholesalerReports || {}
  );
  const wholesalerMonthDateSearchState = useSelector(
    (state) => state?.consumption?.monthDateSearchWholesalerReports || {}
  );
  const wholesalerWeekDateSearchState = useSelector(
    (state) => state?.consumption?.weekDateSearchWholesalerReports || {}
  );

  const barViewAllState = useSelector(
    (state) => state?.barReport?.viewAllBarReports || {}
  );
  const barList = barViewAllState?.data || [];
  const barRecords = barViewAllState?.data?.totalRecords || 0;
  const barPageSize = barViewAllState?.data?.pagesize || 10;

  const barMonthViewAllState = useSelector(
    (state) => state?.barReport?.monthBarReports || {}
  );
  const barMonthList = barMonthViewAllState?.data || [];
  const barMonthRecords = barMonthViewAllState?.data?.totalRecords || 0;
  const barMonthPageSize = barMonthViewAllState?.data?.pagesize || 10;

  const barWeekViewAllState = useSelector(
    (state) => state?.barReport?.weekBarReports || {}
  );
  const barWeekList = barWeekViewAllState?.data || [];
  const barWeekRecords = barWeekViewAllState?.data?.totalRecords || 0;
  const barWeekPageSize = barWeekViewAllState?.data?.pagesize || 10;

  const barDateSearchState = useSelector(
    (state) => state?.barReport?.dateSearchBarReports || {}
  );
  const barMonthDateSearchState = useSelector(
    (state) => state?.barReport?.dateMonthSearchBarReports || {}
  );
  const barWeekDateSearchState = useSelector(
    (state) => state?.barReport?.dateWeekSearchBarReports || {}
  );

  const customerViewAllState = useSelector(
    (state) => state?.customerReport?.viewAllCustomerReports || {}
  );
  const customerList = customerViewAllState?.data?.data || [];
  const customerRecords = customerViewAllState?.data?.totalRecords || 0;
  const customerPageSize = customerViewAllState?.data?.pagesize || 10;

  const customerDateSearchState = useSelector(
    (state) => state?.customerReport?.searchCustomerReports || {}
  );

  // API call function
  const fetchReportData = useCallback(
    (category, timeframe, page = 1) => {
      if (category === CATEGORIES.WHOLESALER) {
        switch (timeframe) {
          case TIME_FRAMES.DAY:
            dispatch(wholesalerReportRequest(page));
            break;
          case TIME_FRAMES.MONTH:
            dispatch(wholesalerMonthReportRequest(page));
            break;
          case TIME_FRAMES.WEEK:
            dispatch(wholesalerWeekReportRequest(page));
            break;
          default:
            break;
        }
      } else if (category === CATEGORIES.BAR_CLUB) {
        switch (timeframe) {
          case TIME_FRAMES.DAY:
            dispatch(barReportRequest(page));
            break;
          case TIME_FRAMES.MONTH:
            dispatch(barMonthReportRequest(page));
            break;
          case TIME_FRAMES.WEEK:
            dispatch(barWeekReportRequest(page));
            break;
          default:
            break;
        }
      } else if (category === CATEGORIES.CUSTOMER) {
        dispatch(customerReportRequest(page));
      }
    },
    [dispatch]
  );

  // Initial data fetch - only for default category and timeframe
  useEffect(() => {
    if (!isDateSearchActive) {
      fetchReportData(selectedCategory, timeFrame, currentPage);
    }
  }, [
    fetchReportData,
    selectedCategory,
    timeFrame,
    currentPage,
    isDateSearchActive,
  ]);

  // Handle timeframe change
  const handleTimeFrameChange = useCallback(
    (newTimeFrame) => {
      setTimeFrame(newTimeFrame);
      setCurrentPage(1);

      if (!isDateSearchActive) {
        fetchReportData(selectedCategory, newTimeFrame, 1);
      }
    },
    [selectedCategory, isDateSearchActive, fetchReportData]
  );

  // Handle category change
  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
    setCurrentPage(1);
    setIsDateSearchActive(false);

    fetchReportData(newCategory, timeFrame, 1);
  };

  useEffect(() => {
    if (isDateSearchActive) {
      if (selectedCategory === CATEGORIES.WHOLESALER) {
        let searchState,
          processedData = [];

        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            searchState = wholesalerDateSearchState;
            break;
          case TIME_FRAMES.MONTH:
            searchState = wholesalerMonthDateSearchState;
            break;
          case TIME_FRAMES.WEEK:
            searchState = wholesalerWeekDateSearchState;
            break;
          default:
            break;
        }

        if (searchState?.success) {
          processedData = preprocessData(searchState.data);
          setRows(processedData);
          setFilteredRecordsCount(searchState.data.totalRecords || 0);
        } else {
          setRows([]);
          setFilteredRecordsCount(0);
        }
      } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
        let searchState,
          barData = [];

        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            searchState = barDateSearchState;
            break;
          case TIME_FRAMES.MONTH:
            searchState = barMonthDateSearchState;
            break;
          case TIME_FRAMES.WEEK:
            searchState = barWeekDateSearchState;
            break;
          default:
            break;
        }

        if (searchState?.success) {
          barData = searchState.data?.data?.bars || [];
          const processedBarData = preprocessBarData(barData);
          setRows(processedBarData);
          setFilteredRecordsCount(barData.length);
        } else {
          setRows([]);
          setFilteredRecordsCount(0);
        }
      } else if (selectedCategory === CATEGORIES.CUSTOMER) {
        if (
          customerDateSearchState?.success &&
          customerDateSearchState.data?.data?.length > 0
        ) {
          const processedData = preprocessCustomerData(
            customerDateSearchState.data
          );
          setRows(processedData);
          setFilteredRecordsCount(
            customerDateSearchState.data.totalRecords || 0
          );
        } else {
          setRows([]);
          setFilteredRecordsCount(0);
        }
      }
    } else {
      // Handle regular data
      if (selectedCategory === CATEGORIES.WHOLESALER) {
        let viewState,
          processedData = [];

        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            viewState = wholesalerViewAllState;
            if (viewState?.success) {
              processedData = preprocessData(wholesalerList, "day");
            }
            break;
          case TIME_FRAMES.MONTH:
            viewState = monthWholesalerViewAllState;
            if (viewState?.success) {
              processedData = preprocessData(monthWholesalerList, "month");
            }
            break;
          case TIME_FRAMES.WEEK:
            viewState = weekWholesalerViewAllState;
            if (viewState?.success) {
              processedData = preprocessData(weekWholesalerList, "week");
            }
            break;
          default:
            break;
        }

        // FIX: Add consistent empty data handling
        if (processedData.length > 0) {
          setRows(processedData);
        } else {
          setRows(emptyList);
        }
      } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
        let viewState,
          combinedBarData = [];

        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            viewState = barViewAllState;
            if (viewState?.success) {
              combinedBarData = barViewAllState.data?.data?.bars || [];
            }
            break;
          case TIME_FRAMES.MONTH:
            viewState = barMonthViewAllState;
            if (viewState?.success) {
              combinedBarData = barMonthViewAllState.data?.data?.bars || [];
            }
            break;
          case TIME_FRAMES.WEEK:
            viewState = barWeekViewAllState;
            if (viewState?.success) {
              combinedBarData = barWeekViewAllState.data?.data?.bars || [];
            }
            break;
          default:
            break;
        }

        if (combinedBarData.length > 0) {
          const preprocessedBarData = preprocessBarData({
            data: { bars: combinedBarData },
          });
          setRows(preprocessedBarData);
        } else {
          setRows(emptyList);
        }
      } else if (selectedCategory === CATEGORIES.CUSTOMER) {
        if (customerViewAllState?.success) {
          const processedData = preprocessCustomerData(
            customerViewAllState.data
          );
          setRows(processedData);
        } else {
          setRows(emptyList);
        }
      }
    }
  }, [
    selectedCategory,
    timeFrame,
    isDateSearchActive,
    wholesalerViewAllState,
    monthWholesalerViewAllState,
    weekWholesalerViewAllState,
    barViewAllState,
    barMonthViewAllState,
    barWeekViewAllState,
    customerViewAllState,
    wholesalerDateSearchState,
    wholesalerMonthDateSearchState,
    wholesalerWeekDateSearchState,
    barDateSearchState,
    barMonthDateSearchState,
    barWeekDateSearchState,
    customerDateSearchState,
  ]);

  useEffect(() => {
    if (selectedCategory === CATEGORIES.WHOLESALER) {
      setTableColumns(getWholesalerColumns(handleViewDetails));
    } else if (selectedCategory === CATEGORIES.CUSTOMER) {
      setTableColumns([...getCustomerColumns]);
    } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
      setTableColumns([...getBarColumns(handleViewBarDetails)]);
    }
  }, [selectedCategory, handleViewDetails, handleViewBarDetails]);

  useEffect(() => {
    let filtered = [];
    let columnsToUse = [];

    if (selectedCategory === CATEGORIES.CUSTOMER) {
      setTableColumns([...getCustomerColumns]);
      setFilteredRows(rows);
      return;
    }

    if (isDateSearchActive) {
      if (selectedCategory === CATEGORIES.WHOLESALER) {
        filtered = rows.filter(
          (row) => row.dataType === timeFrame.toLowerCase()
        );
        columnsToUse = getWholesalerColumns(handleViewDetails);
      } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
        filtered = rows.filter(
          (row) => row.dataType === timeFrame.toLowerCase()
        );
        columnsToUse = getBarColumns(handleViewBarDetails);
      }
      setFilteredRows(filtered);
      setTableColumns(columnsToUse);
      return;
    }

    if (selectedCategory === CATEGORIES.WHOLESALER) {
      filtered = rows.filter((row) => row.dataType === timeFrame.toLowerCase());
      columnsToUse = getWholesalerColumns(handleViewDetails);
    } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
      filtered = rows.filter((row) => row.dataType === timeFrame.toLowerCase());
      columnsToUse = getBarColumns(handleViewBarDetails);
    }

    setFilteredRows(filtered);
    setTableColumns(columnsToUse);
  }, [
    timeFrame,
    selectedCategory,
    rows,
    isDateSearchActive,
    handleViewDetails,
    handleViewBarDetails,
  ]);

  useEffect(() => {
    if (!isDateSearchActive) {
      let processedChartData = [];
      let totalSalesValue = 0;
      let avgSalesValue = 0;

      const formatMonth = (monthIndex) =>
        new Date(2023, monthIndex).toLocaleString("default", { month: "long" });

      if (selectedCategory === CATEGORIES.WHOLESALER) {
        let transactionData = [];
        let viewState;

        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            viewState = wholesalerViewAllState;
            transactionData =
              viewState?.data?.totals?.allWholesalersDayWiseReport || [];
            break;
          case TIME_FRAMES.WEEK:
            viewState = weekWholesalerViewAllState;
            transactionData =
              viewState?.data?.totals?.allWholesalersWeeklyReport || [];
            break;
          case TIME_FRAMES.MONTH:
            viewState = monthWholesalerViewAllState;
            transactionData =
              viewState?.data?.totals?.allWholesalersMonthlyReport || [];
            break;
          default:
            break;
        }

        if (timeFrame === TIME_FRAMES.DAY) {
          totalSalesValue = transactionData.reduce(
            (sum, item) => sum + (item.transactions || 0),
            0
          );
          avgSalesValue =
            transactionData.length > 0
              ? totalSalesValue / transactionData.length
              : 0;
          processedChartData = transactionData.map((item) => ({
            date: item.date,
            Transactions: item.transactions,
          }));
        } else if (timeFrame === TIME_FRAMES.WEEK) {
          totalSalesValue = transactionData.reduce(
            (sum, item) => sum + (item.weeklyTransactions || 0),
            0
          );
          avgSalesValue =
            transactionData.length > 0
              ? totalSalesValue / (transactionData.length * 7)
              : 0;
          processedChartData = transactionData.map((item) => ({
            date: `Week ${item.week}`,
            Transactions: item.weeklyTransactions,
          }));
        } else if (timeFrame === TIME_FRAMES.MONTH) {
          totalSalesValue = transactionData.reduce(
            (sum, item) => sum + (item.monthlyTransactions || 0),
            0
          );
          const totalDays = transactionData.reduce((sum, item) => {
            const [year, month] = item.month.split("-").map(Number);
            return sum + new Date(year, month, 0).getDate();
          }, 0);
          avgSalesValue = totalDays > 0 ? totalSalesValue / totalDays : 0;
          processedChartData = Array.from({ length: 12 }, (_, i) => {
            const existing = transactionData.find((item) => {
              const itemMonth = new Date(item.month + "-01").getMonth();
              return itemMonth === i;
            });
            return {
              date: formatMonth(i),
              Transactions: existing ? existing.monthlyTransactions : 0,
            };
          });
        }
      } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
        let transactionData = [];
        let viewState;

        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            viewState = barViewAllState;
            transactionData =
              viewState?.data?.data?.totals?.dayWiseReport || [];
            break;
          case TIME_FRAMES.WEEK:
            viewState = barWeekViewAllState;
            transactionData =
              viewState?.data?.data?.totals?.weekWiseReport || [];
            break;
          case TIME_FRAMES.MONTH:
            viewState = barMonthViewAllState;
            transactionData =
              viewState?.data?.data?.totals?.monthWiseReport || [];
            break;
          default:
            break;
        }

        if (timeFrame === TIME_FRAMES.DAY) {
          totalSalesValue = transactionData.reduce(
            (sum, item) => sum + (item.totalShots || 0),
            0
          );
          avgSalesValue =
            transactionData.length > 0
              ? totalSalesValue / transactionData.length
              : 0;
          processedChartData = transactionData.map((item) => ({
            date: item.date,
            ShotsSold: item.totalShots,
          }));
        } else if (timeFrame === TIME_FRAMES.WEEK) {
          totalSalesValue = transactionData.reduce(
            (sum, item) => sum + (item.totalShots || 0),
            0
          );
          avgSalesValue =
            transactionData.length > 0
              ? totalSalesValue / (transactionData.length * 7)
              : 0;
          processedChartData = transactionData.map((item) => ({
            date: `Week ${item.week}`,
            ShotsSold: item.totalShots,
          }));
        } else if (timeFrame === TIME_FRAMES.MONTH) {
          totalSalesValue = transactionData.reduce(
            (sum, item) => sum + (item.totalShots || 0),
            0
          );
          const totalDays = transactionData.reduce((sum, item) => {
            const [year, month] = item.month.split("-").map(Number);
            return sum + new Date(year, month, 0).getDate();
          }, 0);
          avgSalesValue = totalDays > 0 ? totalSalesValue / totalDays : 0;
          processedChartData = Array.from({ length: 12 }, (_, i) => {
            const existing = transactionData.find((item) => {
              const itemMonth = new Date(item.month + "-01").getMonth();
              return itemMonth === i;
            });
            return {
              date: formatMonth(i),
              ShotsSold: existing ? existing.totalShots : 0,
            };
          });
        }
      }

      setChartData(processedChartData);
      setTotalSales(totalSalesValue);
      setAvgSalesPerDay(avgSalesValue);
    }
  }, [
    timeFrame,
    selectedCategory,
    isDateSearchActive,
    wholesalerViewAllState,
    monthWholesalerViewAllState,
    weekWholesalerViewAllState,
    barViewAllState,
    barMonthViewAllState,
    barWeekViewAllState,
  ]);

  const handleReset = () => {
    reset({ startDate: "", endDate: "" });
    setCurrentPage(1);
    setIsDateSearchActive(false);
    fetchReportData(selectedCategory, timeFrame, 1);
  };

  const handleSubmitDates = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date must be before end date");
      return;
    }

    const formattedStartDate = startDate;
    const formattedEndDate = endDate;

    setIsDateSearchActive(true);
    setCurrentPage(1);

    if (selectedCategory === CATEGORIES.WHOLESALER) {
      const searchRequest = {
        pageNo: 1,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };

      switch (timeFrame) {
        case TIME_FRAMES.DAY:
          dispatch(wholesalerReportDateSearchRequest(searchRequest));
          break;
        case TIME_FRAMES.MONTH:
          dispatch(wholesalerMonthReportDateSearchRequest(searchRequest));
          break;
        case TIME_FRAMES.WEEK:
          dispatch(wholesalerWeekReportDateSearchRequest(searchRequest));
          break;
        default:
          break;
      }
    } else if (selectedCategory === CATEGORIES.BAR_CLUB) {
      const searchRequest = {
        pageNo: 1,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      };

      switch (timeFrame) {
        case TIME_FRAMES.DAY:
          dispatch(barReportDateSearchRequest(searchRequest));
          break;
        case TIME_FRAMES.MONTH:
          dispatch(barMonthReportDateSearchRequest(searchRequest));
          break;
        case TIME_FRAMES.WEEK:
          dispatch(barWeekReportDateSearchRequest(searchRequest));
          break;
        default:
          break;
      }
    } else if (selectedCategory === CATEGORIES.CUSTOMER) {
      dispatch(
        customerReportSearchRequest({
          pageNo: 1,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        })
      );
    }
  };

  //Loading
  const isLoading = useMemo(() => {
    if (selectedCategory === CATEGORIES.WHOLESALER) {
      if (isDateSearchActive) {
        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            return wholesalerDateSearchState.inProgress;
          case TIME_FRAMES.MONTH:
            return wholesalerMonthDateSearchState.inProgress;
          case TIME_FRAMES.WEEK:
            return wholesalerWeekDateSearchState.inProgress;
        }
      } else {
        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            return wholesalerViewAllState.inProgress;
          case TIME_FRAMES.MONTH:
            return monthWholesalerViewAllState.inProgress;
          case TIME_FRAMES.WEEK:
            return weekWholesalerViewAllState.inProgress;
        }
      }
    }

    if (selectedCategory === CATEGORIES.BAR_CLUB) {
      if (isDateSearchActive) {
        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            return barDateSearchState.inProgress;
          case TIME_FRAMES.MONTH:
            return barMonthDateSearchState.inProgress;
          case TIME_FRAMES.WEEK:
            return barWeekDateSearchState.inProgress;
        }
      } else {
        switch (timeFrame) {
          case TIME_FRAMES.DAY:
            return barViewAllState.inProgress;
          case TIME_FRAMES.MONTH:
            return barMonthViewAllState.inProgress;
          case TIME_FRAMES.WEEK:
            return barWeekViewAllState.inProgress;
        }
      }
    }

    if (selectedCategory === CATEGORIES.CUSTOMER) {
      return isDateSearchActive
        ? customerDateSearchState.inProgress
        : customerViewAllState.inProgress;
    }

    return false;
  }, [
    selectedCategory,
    timeFrame,
    isDateSearchActive,

    wholesalerViewAllState.inProgress,
    monthWholesalerViewAllState.inProgress,
    weekWholesalerViewAllState.inProgress,
    wholesalerDateSearchState.inProgress,
    wholesalerMonthDateSearchState.inProgress,
    wholesalerWeekDateSearchState.inProgress,

    barViewAllState.inProgress,
    barMonthViewAllState.inProgress,
    barWeekViewAllState.inProgress,
    barDateSearchState.inProgress,
    barMonthDateSearchState.inProgress,
    barWeekDateSearchState.inProgress,

    customerViewAllState.inProgress,
    customerDateSearchState.inProgress,
  ]);

  const handleExcelDownload = () => {
    const workbook = prepareExcelData(
      filteredRows,
      selectedCategory,
      timeFrame
    );
    XLSX.writeFile(
      workbook,
      `Sales_Report_${selectedCategory}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`
    );
  };

  const normalizedChartData = [
    {
      id:
        selectedCategory === CATEGORIES.BAR_CLUB ? "Shots Sold" : "Sales Data",
      data: chartData.map((item) => ({
        x: item.date,
        y:
          selectedCategory === CATEGORIES.BAR_CLUB
            ? item.ShotsSold || 0
            : item.Transactions || 0,
      })),
    },
  ];

  return (
    <Container maxWidth="xll" sx={reportStyles.containerMarginStyles}>
      <Box py={4}>
        <Grid item xs={12} sm={12} md={4} lg={8} xl={10}>
          <Box className="headingTable">
            <h1>Business Reports</h1>
          </Box>
        </Grid>

        {/* Filters Section */}
        <Paper elevation={2} sx={reportStyles.paperStyles}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="category-select-label">Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Category"
                >
                  <MenuItem value={CATEGORIES.BAR_CLUB}>Bar/Club</MenuItem>
                  <MenuItem value={CATEGORIES.WHOLESALER}>Wholesaler</MenuItem>
                  <MenuItem value={CATEGORIES.CUSTOMER}>Customer</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={7}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <Controller
                  name="startDate"
                  control={control}
                  rules={{
                    required: "Start Date is required",
                    validate: (value) => {
                      const end = watch("endDate");
                      if (end && new Date(value) > new Date(end)) {
                        return "Start date must be before or equal to End date";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Start Date"
                      type="date"
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />

                <Typography variant="body1" sx={reportStyles.body1Styles}>
                  To
                </Typography>

                <Controller
                  name="endDate"
                  control={control}
                  rules={{
                    required: "End Date is required",
                    validate: (value) => {
                      const start = watch("startDate");
                      if (start && new Date(value) < new Date(start)) {
                        return "End date must be after or equal to Start date";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="End Date"
                      type="date"
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSubmitDates}
                  fullWidth
                  sx={reportStyles.statckStyles}
                >
                  Submit
                </Button>

                <Tooltip title="Reset Filters">
                  <IconButton onClick={handleReset} color="default">
                    ↺
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Stats and Chart Section */}
        {selectedCategory !== CATEGORIES.CUSTOMER && !isDateSearchActive && (
          <Paper elevation={3} sx={reportStyles.chartSectionPaperStyles}>
            <Box p={{ xs: 2, md: 3 }}>
              <Grid container spacing={2} alignItems="center" mb={3}>
                <Grid item xs={12} md={8}>
                  <Typography
                    variant="h6"
                    fontWeight={500}
                    sx={reportStyles.chartSectionTypographyStyles}
                  >
                    Chart Orders
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={reportStyles.chartSectionBoxStyles}>
                    <Button
                      onClick={() => setTimeFrame(TIME_FRAMES.MONTH)}
                      sx={{
                        backgroundColor:
                          timeFrame === TIME_FRAMES.MONTH ? "#f5f2f2" : "#ccc",
                        color: "#000",
                        flex: 1,
                        fontSize: { xs: "9px", sm: "12px" },
                        gap: { xs: "2px", sm: "8px" },
                      }}
                    >
                      Month
                    </Button>
                    <Button
                      onClick={() => setTimeFrame(TIME_FRAMES.WEEK)}
                      sx={{
                        backgroundColor:
                          timeFrame === TIME_FRAMES.WEEK ? "#f5f2f2" : "#ccc",
                        color: "#000",
                        flex: 1,
                        fontSize: { xs: "9px", sm: "12px" },
                        minWidth: { xs: "45px", sm: "70px" },
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      Week
                    </Button>
                    <Button
                      onClick={() => setTimeFrame(TIME_FRAMES.DAY)}
                      sx={{
                        backgroundColor:
                          timeFrame === TIME_FRAMES.DAY ? "#f5f2f2" : "#ccc",
                        color: "#000",
                        flex: 1,
                        fontSize: { xs: "9px", sm: "12px" },
                        minWidth: { xs: "45px", sm: "70px" },
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      Day
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={reportStyles.displayStyles}>
                <Box sx={reportStyles.paddingLeft}>
                  <Box sx={reportStyles.display}>
                    <Avatar
                      variant="square"
                      style={reportStyles.avatarBackGroundColor}
                    >
                      <img
                        src={BottlesIcon}
                        alt="Bottles"
                        style={reportStyles.bottleColor}
                      />
                    </Avatar>
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight={600}
                      pt={1}
                    >
                      {totalSales.toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    component="p"
                    paddingLeft={2}
                    color="#0056d2"
                  >
                    {selectedCategory === CATEGORIES.BAR_CLUB
                      ? "Total Shots Sold"
                      : selectedCategory === CATEGORIES.CUSTOMER
                      ? "Total Orders"
                      : "Total Transactions"}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={reportStyles.display}>
                    <Avatar
                      variant="square"
                      style={reportStyles.avatarBackgroundColor}
                    >
                      <img
                        src={GlassIcon}
                        alt="Glass"
                        style={reportStyles.reportImageStyles}
                      />
                    </Avatar>
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight={600}
                      pt={1}
                    >
                      {avgSalesPerDay.toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    component="p"
                    paddingLeft={2}
                    color="#0056d2"
                  >
                    {selectedCategory === CATEGORIES.BAR_CLUB
                      ? `Avg Shots per ${timeFrame}`
                      : selectedCategory === CATEGORIES.CUSTOMER
                      ? `Avg Orders per ${timeFrame}`
                      : `Avg Transactions per ${timeFrame}`}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                ...reportStyles.perfectScrollBarBoxStyles,
                height: 350,
                width: "100%",
                padding: "0 16px",
              }}
            >
              <PerfectScrollbar>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={
                      normalizedChartData[0]?.data?.some((point) => point.y > 0)
                        ? normalizedChartData[0].data.map(({ x, y }) => ({
                            date: x,
                            Transactions: y,
                          }))
                        : [{ date: "No Data", Transactions: 0 }]
                    }
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#333" }}
                      axisLine={{ stroke: "#e0e0e0" }}
                    />
                    <YAxis
                      tick={{ fill: "#333" }}
                      axisLine={{ stroke: "#e0e0e0" }}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #f0f0f0",
                        borderRadius: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Transactions"
                      stroke="#1b66ef"
                      strokeWidth={3}
                      dot={{ fill: "#1b66ef", r: 6 }}
                      activeDot={{
                        r: 8,
                        stroke: "#1b66ef",
                        strokeWidth: 2,
                        fill: "white",
                      }}
                      name={
                        selectedCategory === CATEGORIES.BAR_CLUB
                          ? "Shots Sold"
                          : "Transactions"
                      }
                    />
                  </LineChart>
                </ResponsiveContainer>
              </PerfectScrollbar>
            </Box>
          </Paper>
        )}
        {/* Table Section */}
        <Paper elevation={3} sx={reportStyles.tablePaperStyles}>
          <Box
            p={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" sx={reportStyles.tableTypographyColor}>
              <h2>{selectedCategory} Sales Data</h2>
            </Typography>

            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExcelDownload}
              sx={reportStyles.buttonStyles}
            >
              Download Excel Report
            </Button>
          </Box>

          {selectedCategory === CATEGORIES.WHOLESALER && (
            <CustomDataGrid
              rows={filteredRows}
              columns={tableColumns}
              getRowId={(row) => row.id}
              pageSize={wholesalerPageSize}
              pagination={true}
              loading={wholesalerViewAllState?.inProgress}
              autoHeight
              onCellClick={(params) => {
                if (params.field === "action") {
                  handleViewDetails(params.row);
                }
              }}
              sx={reportStyles.customGridStyles}
            />
          )}

          {selectedCategory === CATEGORIES.CUSTOMER && (
            <CustomDataGrid
              rows={filteredRows}
              columns={tableColumns}
              getRowId={(row) => row.id}
              pageSize={customerPageSize}
              pagination={true}
              loading={customerViewAllState?.inProgress}
              autoHeight
              sx={reportStyles.customGridStyles}
            />
          )}

          {selectedCategory === CATEGORIES.BAR_CLUB && (
            <CustomDataGrid
              rows={filteredRows}
              columns={tableColumns}
              getRowId={(row) => row.id}
              pageSize={barPageSize}
              pagination={true}
              autoHeight
              loading={isLoading}
              sx={reportStyles.customGridStyles}
              onCellClick={(params) => {
                if (params.field === "action") {
                  handleViewBarDetails(params.row);
                }
              }}
            />
          )}

          <Grid container>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Box px={2}>
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalRecords={
                    isDateSearchActive
                      ? filteredRecordsCount
                      : selectedCategory === CATEGORIES.WHOLESALER
                      ? timeFrame === TIME_FRAMES.MONTH
                        ? monthTotalRecords
                        : timeFrame === TIME_FRAMES.WEEK
                        ? weekTotalRecords
                        : totalRecords
                      : selectedCategory === CATEGORIES.CUSTOMER
                      ? customerRecords
                      : barRecords
                  }
                  pageSize={
                    selectedCategory === CATEGORIES.WHOLESALER
                      ? timeFrame === TIME_FRAMES.MONTH
                        ? monthWholesalerPageSize
                        : timeFrame === TIME_FRAMES.WEEK
                        ? weekWholesalerPageSize
                        : wholesalerPageSize
                      : selectedCategory === CATEGORIES.CUSTOMER
                      ? customerPageSize
                      : barPageSize
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      {/* Modal for displaying wholesaler details */}
      <WholesalerDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        wholesalerData={selectedWholesaler}
        currentTimeFrame={currentTimeFrame}
      />
      <BarDetailsModal
        open={isBarModalOpen}
        onClose={handleCloseBarModal}
        barData={selectedBar}
        currentTimeFrame={currentTimeFrame}
      />
    </Container>
  );
};

export default SalesReport;
